import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import {
  MdAttachMoney,
  MdCheckCircle,
  MdErrorOutline,
  MdHourglassEmpty,
  MdRefresh,
  MdSearch,
  MdShoppingCart,
  MdSync,
  MdWarningAmber,
} from "react-icons/md";
import moment from "moment";
import { useAuth0 } from "@auth0/auth0-react";
import { MainTable } from "components/Table/MainTable";
import Card from "components/card/Card.js";
import StatCard from "./components/StatCard";
import StatusBadge from "./components/StatusBadge";
import AloOrderDrawer from "./components/AloOrderDrawer";
import { describeError, isApiMissing } from "./api";
import { useAloOrders, useAloSummary } from "./hooks";
import { ALO_STATUSES, displayPhone, dubaiTime, money, statusLabel, toDateInput } from "./format";

const PER_PAGE_OPTIONS = [25, 50, 100];

function useDebounced(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function ProblemCard({ error, onRetry }) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const missing = isApiMissing(error);
  const expired = error && error.status === 401;

  const refreshSession = async () => {
    try {
      const token = await getAccessTokenSilently({ audience: "https://api.b2connect.me" });
      localStorage.setItem("auth0_token", token);
      onRetry();
    } catch (e) {
      loginWithRedirect();
    }
  };

  return (
    <Alert
      status={missing ? "info" : "warning"}
      variant="left-accent"
      borderRadius="16px"
      flexDirection={{ base: "column", md: "row" }}
      alignItems={{ base: "flex-start", md: "center" }}
      gap={3}
      mb="20px"
    >
      <AlertIcon />
      <Box flex="1">
        <AlertTitle>{missing ? "alo store API not available" : "Couldn't load alo WiFi orders"}</AlertTitle>
        <AlertDescription display="block" fontSize="sm">
          {missing
            ? "The management API doesn't have the alo WiFi store endpoints yet. They appear once the release with the web store is deployed."
            : describeError(error)}
        </AlertDescription>
      </Box>
      {expired ? (
        <Button colorScheme="orange" onClick={refreshSession}>
          Refresh session
        </Button>
      ) : (
        <Button leftIcon={<MdRefresh />} onClick={onRetry}>
          Try again
        </Button>
      )}
    </Alert>
  );
}

export default function AloOrders() {
  const { user, isLoading: userLoading } = useAuth0();
  const [mounted, setMounted] = useState(false);
  const [from, setFrom] = useState(toDateInput(moment().subtract(29, "days")));
  const [to, setTo] = useState(toDateInput(moment()));
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 400);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_OPTIONS[0]);
  const [selected, setSelected] = useState(null);
  const drawer = useDisclosure();

  // Same gate as the other admin pages: wait for Auth0 to put the API token in localStorage.
  useEffect(() => {
    const token = localStorage.getItem("auth0_token");
    if (user && !userLoading && token) setMounted(true);
  }, [user, userLoading]);

  useEffect(() => setPage(1), [from, to, status, q, perPage]);

  const { summary, error: summaryError, mutate: refreshSummary } = useAloSummary(mounted, from, to);
  const { orders, error: ordersError, isFirstLoad, mutate: refreshOrders } = useAloOrders(mounted, {
    from,
    to,
    status,
    q,
    page: page - 1,
    size: perPage,
  });

  const refreshAll = () => {
    refreshSummary();
    refreshOrders();
  };

  const items = (orders && orders.items) || [];
  const total = (orders && orders.total) || 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const byStatus = (summary && summary.byStatus) || {};
  const currency = (summary && summary.currency) || "AED";
  const error = ordersError || summaryError;

  const columns = useMemo(
    () => [
      { Header: "Order #", accessor: "orderId", Cell: ({ value }) => <Text fontWeight="700">#{value}</Text> },
      { Header: "Date (Dubai)", accessor: "createdAtLabel" },
      { Header: "Phone", accessor: "phoneLabel" },
      { Header: "Bundle", accessor: "productName" },
      { Header: "Amount", accessor: "amountLabel" },
      { Header: "Status", accessor: "status", Cell: ({ value }) => <StatusBadge status={value} /> },
      {
        Header: "Code",
        accessor: "voucherIssued",
        Cell: ({ value }) => (
          <Text fontWeight="600" color={value ? "green.500" : "secondaryGray.600"}>
            {value ? "Issued" : "No"}
          </Text>
        ),
      },
    ],
    []
  );

  const data = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        createdAtLabel: dubaiTime(row.createdAt),
        phoneLabel: displayPhone(row.phone),
        amountLabel: money(row.amount, row.currency || currency),
      })),
    [items, currency]
  );

  const csv = useMemo(
    () =>
      items.map((row) => ({
        order: row.orderId,
        created_dubai: dubaiTime(row.createdAt),
        phone: row.phone,
        email: row.email || "",
        bundle: row.productName,
        amount: row.amount,
        status: statusLabel(row.status),
        paid_dubai: row.paidAt ? dubaiTime(row.paidAt) : "",
        payby_order: row.paymentOrderNo || "",
        code_issued: row.voucherIssued ? "yes" : "no",
      })),
    [items]
  );

  const cards = [
    {
      key: "PAID",
      name: "Paid orders",
      value: summary ? summary.paidOrders : "—",
      hint: summary ? `of ${summary.orders} orders` : null,
      icon: MdShoppingCart,
      iconColor: "orange.400",
    },
    {
      key: "REVENUE",
      name: "Revenue",
      value: summary ? money(summary.revenue, currency) : "—",
      icon: MdAttachMoney,
      iconColor: "green.500",
    },
    { key: "DELIVERED", name: "Delivered", icon: MdCheckCircle, iconColor: "green.500" },
    { key: "NEEDS_ATTENTION", name: "Needs attention", icon: MdWarningAmber, iconColor: "orange.500" },
    { key: "PROCESSING", name: "Processing", icon: MdSync, iconColor: "blue.500" },
    { key: "AWAITING_PAYMENT", name: "Awaiting payment", icon: MdHourglassEmpty, iconColor: "gray.500" },
    { key: "PAYMENT_FAILED", name: "Payment failed", icon: MdErrorOutline, iconColor: "red.500" },
  ];

  return (
    <Box pt={{ base: "180px", md: "80px", xl: "80px" }}>
      {error ? <ProblemCard error={error} onRetry={refreshAll} /> : null}

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="20px" mb="20px">
        {cards.map((card) => {
          const isStatus = Boolean(ALO_STATUSES[card.key]);
          const count = isStatus ? byStatus[card.key] || 0 : null;
          return (
            <StatCard
              key={card.key}
              name={card.name}
              value={isStatus ? (summary ? count : "—") : card.value}
              hint={card.hint}
              icon={card.icon}
              iconColor={card.iconColor}
              loaded={Boolean(summary) || Boolean(error) || !mounted}
              highlight={card.key === "NEEDS_ATTENTION" && count > 0}
              active={isStatus && status === card.key}
              onClick={isStatus ? () => setStatus((current) => (current === card.key ? "" : card.key)) : undefined}
            />
          );
        })}
      </SimpleGrid>

      <Card mb="20px" p="20px">
        <Flex gap={4} direction={{ base: "column", lg: "row" }} align={{ base: "stretch", lg: "flex-end" }}>
          <FormControl flex="2">
            <FormLabel fontSize="sm" fontWeight="700">
              Search
            </FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none" children={<MdSearch color="gray" />} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Order #, phone, email or PayBy order no."
              />
            </InputGroup>
          </FormControl>
          <FormControl flex="1">
            <FormLabel fontSize="sm" fontWeight="700">
              Status
            </FormLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {Object.entries(ALO_STATUSES).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl flex="1">
            <FormLabel fontSize="sm" fontWeight="700">
              From
            </FormLabel>
            <Input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
          </FormControl>
          <FormControl flex="1">
            <FormLabel fontSize="sm" fontWeight="700">
              To
            </FormLabel>
            <Input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} />
          </FormControl>
          <Tooltip label="Refresh now (also refreshes every 30 seconds)">
            <IconButton aria-label="Refresh" icon={<MdRefresh />} onClick={refreshAll} colorScheme="orange" variant="outline" />
          </Tooltip>
        </Flex>
      </Card>

      <MainTable
        loaded={!isFirstLoad || Boolean(error)}
        data={data}
        columns={columns}
        setPage={setPage}
        setPerPage={setPerPage}
        currentpage={page}
        perPage={perPage}
        perPageOptions={PER_PAGE_OPTIONS}
        totalPage={totalPages}
        exports={csv}
        name={`alo WiFi Orders (${total})`}
        drawer
        handleDrawer={(row) => {
          setSelected(row);
          drawer.onOpen();
        }}
      />

      <AloOrderDrawer
        orderId={selected ? selected.orderId : null}
        preview={selected}
        isOpen={drawer.isOpen}
        onClose={drawer.onClose}
        onChanged={refreshAll}
      />
    </Box>
  );
}
